import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';
import useAdminDialog from '../components/AdminDialog';

const STATUS_BADGE = { pending: 'badge-blue', published: 'badge-green', rejected: 'badge-red' };

export default function MarketplaceReview() {
  const { copy } = useAdminLanguage();
  const marketplaceReviewCopy = copy.marketplaceReview;
  const [tab, setTab] = useState('services');
  const [status, setStatus] = useState('all');
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirmAction, dialog } = useAdminDialog();

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const serviceReq = api.get('/admin/marketplace/services', { params: { status } });
    const productReq = api.get('/admin/marketplace/products', { params: { status } });
    Promise.all([serviceReq, productReq])
      .then(([serviceRes, productRes]) => {
        setServices(serviceRes.data.services || []);
        setProducts(productRes.data.products || []);
      })
      .catch(err => setError(err.response?.data?.error || marketplaceReviewCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [status, marketplaceReviewCopy.errors.loadFailed]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (type, id, nextStatus) => {
    await api.post(`/admin/marketplace/${type}/${id}/status`, { status: nextStatus });
    load();
  };

  const remove = async (type, id) => {
    if (!await confirmAction({
      title: marketplaceReviewCopy.deleteConfirm.title,
      message: marketplaceReviewCopy.deleteConfirm.message,
      variant: 'danger',
    })) return;
    await api.delete(`/admin/marketplace/${type}/${id}`);
    load();
  };

  const rows = tab === 'services' ? services : products;

  return (
    <div>
      {dialog}
      <div className="page-title">{marketplaceReviewCopy.title}</div>
      {error && <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</div>}
      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${tab === 'services' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('services')}>{marketplaceReviewCopy.tabs.services}</button>
          <button className={`btn btn-sm ${tab === 'products' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('products')}>{marketplaceReviewCopy.tabs.products}</button>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <option value="all">{marketplaceReviewCopy.allStatus}</option>
            <option value="pending">{marketplaceReviewCopy.status.pending}</option>
            <option value="published">{marketplaceReviewCopy.status.published}</option>
            <option value="rejected">{marketplaceReviewCopy.status.rejected}</option>
          </select>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center', marginLeft: 'auto' }}>{marketplaceReviewCopy.showing(rows.length)}</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{marketplaceReviewCopy.columns.id}</th><th>{marketplaceReviewCopy.columns.title}</th><th>{marketplaceReviewCopy.columns.category}</th><th>{marketplaceReviewCopy.columns.publisher}</th><th>{marketplaceReviewCopy.columns.priceMethod}</th><th>{marketplaceReviewCopy.columns.description}</th><th>{marketplaceReviewCopy.columns.status}</th><th>{marketplaceReviewCopy.columns.createdAt}</th><th>{marketplaceReviewCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{marketplaceReviewCopy.loading}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{marketplaceReviewCopy.empty}</td></tr>
              ) : rows.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{ fontWeight: 600 }}>{item.title}</td>
                  <td>{item.category}</td>
                  <td>{tab === 'services' ? (item.provider_name || '-') : (item.seller_name || '-')}</td>
                  <td>{tab === 'services' ? `${item.price || '-'} · ${item.method || '-'}` : `${item.price || '-'} · ${item.condition || '-'}`}</td>
                  <td style={{ maxWidth: 260, color: '#555' }}>{item.description}</td>
                  <td><span className={`badge ${STATUS_BADGE[item.status] || 'badge-gray'}`}>{marketplaceReviewCopy.status[item.status] || item.status}</span></td>
                  <td style={{ color: '#6b7280' }}>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {item.status !== 'published' && <button className="btn btn-sm btn-primary" onClick={() => updateStatus(tab, item.id, 'published')}>{marketplaceReviewCopy.actions.publish}</button>}
                      {item.status !== 'rejected' && <button className="btn btn-sm btn-outline" onClick={() => updateStatus(tab, item.id, 'rejected')}>{marketplaceReviewCopy.actions.reject}</button>}
                      {item.status !== 'pending' && <button className="btn btn-sm btn-outline" onClick={() => updateStatus(tab, item.id, 'pending')}>{marketplaceReviewCopy.actions.returnPending}</button>}
                      <button className="btn btn-sm btn-danger" onClick={() => remove(tab, item.id)}>{marketplaceReviewCopy.actions.delete}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
